import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import { OutlineStyles } from '../../styles';
import { sidenavTokens } from '../../tokens';
import { SideNavItemProps } from './SideNavItem';
import { resolveNestingPadding } from '../../helpers';

export const StyledSideNavItemLabel = styled.span`
  flex-grow: 1;
  flex-shrink: 0;
  line-height: ${sidenavTokens.ItemFontLeadingBaseDefault};
`;

export const StyledSideNavNewTabIcon = styled.span`
  color: ${sidenavTokens.NewTabFontColorBaseDefault};
  margin-left: auto;
  padding-left: 8px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const StyledSideNavBubbleWrapper = styled.span`
  margin-left: 10px;
`;

type StyledSideNavItemProps = Omit<SideNavItemProps, 'title' | 'url' | 'imageIcon'> & {
  asGroupItem?: boolean;
};

const SideNavItemStyles = css<StyledSideNavItemProps>`
  align-items: center;
  background-color: transparent;
  border: none;
  color: ${sidenavTokens.FontColorBaseDefault};
  cursor: pointer;
  display: ${({ isVisible, asGroupItem }) => {
    if (asGroupItem) {
      return isVisible ? 'flex' : 'none';
    }
    return 'flex';
  }};
  font-size: ${sidenavTokens.ItemFontSizeBaseDefault};
  font-weight: ${sidenavTokens.GroupItemFontWeightBaseDefault};
  height: auto;
  justify-content: space-between;
  line-height: ${sidenavTokens.ItemFontLeadingBaseDefault};
  padding-bottom: ${sidenavTokens.ItemPaddingBaseOpenVertical};
  padding-left: ${({ nestingLevel, asGroupItem }) =>
    resolveNestingPadding({ nestingLevel, type: asGroupItem ? 'group' : 'item' })};
  padding-right: ${sidenavTokens.ItemSpacingPaddingHorizontalBaseDefault};
  padding-top: ${sidenavTokens.ItemPaddingBaseOpenVertical};
  margin-left: 0;
  text-align: start;
  text-decoration: none;
  transition: ${`background-color ${sidenavTokens.TransitionDurationBaseDefault} ${sidenavTokens.TransitionCurveBaseDefault},
        width ${sidenavTokens.TransitionDurationBaseDefault} ${sidenavTokens.TransitionCurveBaseDefault},
        opacity 1s ${sidenavTokens.TransitionCurveBaseDefault}`};
  width: ${({ asGroupItem, isVisible }) => (asGroupItem && isVisible !== undefined && !isVisible ? '0' : '100%')};
  opacity: ${({ asGroupItem, isVisible }) => (asGroupItem && isVisible !== undefined && !isVisible ? '0' : '1')};

  &:hover {
    background-color: ${sidenavTokens.ItemBackgroundColorActiveHover};
    text-decoration: none;
  }

  ${OutlineStyles}

  ${({ isActive }) =>
    isActive &&
    css`
      background-color: ${sidenavTokens.ItemBackgroundColorActiveDefault};
      color: ${sidenavTokens.FontColorBaseDefault};
      font-weight: ${sidenavTokens.GroupItemFontWeightBaseActive};
      border-left: 0;
      box-shadow: ${sidenavTokens.ItemBorderWidthBaseDown} 0 0 0 ${sidenavTokens.ItemBorderColorBaseDown} inset;
    `}

  ${({ noStripe }) =>
    noStripe &&
    css`
      border-left-color: transparent;
    `}

  ${({ sticky }) =>
    sticky &&
    css`
      padding-top: ${sidenavTokens.ItemSpacingPaddingVerticalBaseDefault};
      padding-bottom: ${sidenavTokens.ItemSpacingPaddingVerticalBaseDefault};
    `}

  ${({ asGroupItem }) =>
    asGroupItem &&
    css`
      &:focus-visible {
        border-radius: ${sidenavTokens.GroupItemBorderRadiusVisibleFocus};
        margin-right: ${sidenavTokens.GroupItemMarginRightVisibleFocus};
        padding-right: ${sidenavTokens.GroupItemPaddingRightVisibleFocus};
      }
    `}
`;

export const StyledSideNavItemAnchor = styled.a.withConfig<StyledSideNavItemProps>({
  shouldForwardProp: (prop, dv) =>
    !['isActive', 'isVisible', 'imageIcon', 'noStripe', 'nestingLevel', 'asGroupItem'].includes(prop) || dv(prop),
})`
  ${SideNavItemStyles}
`;

export const StyledSideNavItemLink = styled(Link).withConfig<StyledSideNavItemProps>({
  shouldForwardProp: (prop, dv) =>
    !['isActive', 'isVisible', 'imageIcon', 'noStripe', 'nestingLevel', 'asGroupItem'].includes(prop) || dv(prop),
})`
  ${SideNavItemStyles}
`;
